import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { FALLBACK_IMAGE as FALLBACK } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal } from '../components/Reveal';
import { Img } from '../components/Img';
import { useCollections } from '../lib/useCatalog';
import { collectionCover } from '../lib/collectionCovers';

/**
 * Indice delle collezioni. Il catalogo filtrabile di tutte le referenze
 * vive in /catalogo: qui resta solo l'asse "collezione", con le 14 che
 * hanno un racconto distinte dalle altre, che sono raggruppamenti
 * derivati dal titolo del feed senza voce di marca.
 */
export default function Collections() {
  const { t, lang } = useLang();
  const cols = useCollections(lang);
  const items = cols.data?.items ?? [];
  // `serie: True` distingue le 83 serie (voce breve) dalle collezioni con
  // racconto esteso: nell'hub le prime restano tra le "altre serie".
  const curated = items.filter((c) => c.story && !c.b2b && !c.story.serie);
  const others = items.filter((c) => (!c.story || c.story.serie) && !c.b2b);

  return (
    <>
      <Seo
        title={t('collections.title')}
        description={t('collections.lead')}
        path="/collezioni"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: window.location.origin },
            { '@type': 'ListItem', position: 2, name: t('collections.title') },
          ],
        }}
      />

      <header className="py-14 md:py-24">
        <div className="wrap">
          <h1 className="display-xl max-w-[14ch]">{t('collections.title')}</h1>
          <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">
            {t('collections.lead')}
          </p>
          <p className="accento mt-5 text-xl text-inchiostro/70">{t('collections.since')}</p>
        </div>
      </header>

      {/* niente intestazione "Con un racconto" su una griglia vuota mentre la
          fetch e' in volo: la sezione compare quando ha qualcosa da mostrare */}
      {cols.isPending ? null : cols.isError ? (
        <p data-testid="collections-error" className="wrap py-20 text-center font-display text-2xl text-inchiostro/50">
          {t('collections.loadError')}
        </p>
      ) : (
        <section data-testid="curated-collections" className="py-14 md:py-20">
          <div className="wrap">
            <p className="accento mb-9 text-2xl text-inchiostro/85">{t('collections.curated')}</p>
            <div className="grid gap-x-7 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
              {curated.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`/collezioni/${c.slug}`}
                    data-testid={`collection-card-${c.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[5/4] overflow-hidden bg-carta-2">
                      {collectionCover(c.slug) ? (
                        /* scatto d'ambiente gia' in 5:4 (ASSETS A-10, vedi collectionCovers) */
                        <Img
                          src={collectionCover(c.slug, '-54')}
                          alt={c.name}
                          width={500}
                          aspectRatio={5 / 4}
                          sizes="(max-width:640px) 100vw, (max-width:1024px) 50vw, 33vw"
                          className="img-zoom h-full w-full object-cover"
                        />
                      ) : (
                        <Img
                          src={c.images?.[0] || FALLBACK}
                          alt={c.name}
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK;
                          }}
                          className="img-zoom h-full w-full object-contain p-9 mix-blend-multiply"
                        />
                      )}
                      {c.story?.year && (
                        <span
                          className={`cifre absolute left-4 top-4 text-[14px] font-bold ${
                            collectionCover(c.slug)
                              ? 'bg-carta/90 px-2 py-1 text-inchiostro/70'
                              : 'text-inchiostro/40'
                          }`}
                        >
                          {c.story.year}
                        </span>
                      )}
                    </div>
                    <div className="flex items-start justify-between gap-5 pt-5">
                      <div>
                        <h2 className="font-display text-3xl transition-colors group-hover:text-marsala">
                          {c.name}
                        </h2>
                        <p className="mt-2 font-display text-base italic text-inchiostro/60">
                          {c.story?.claim}
                        </p>
                        <p className="cifre mt-3 text-[14px] font-semibold text-inchiostro/70">
                          {c.product_count} {t('common.products')}
                        </p>
                      </div>
                      <ArrowUpRight
                        size={19}
                        strokeWidth={1.4}
                        className="mt-2 shrink-0 text-marsala transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>

            {others.length > 0 && (
              <div data-testid="plain-collections" className="mt-16 pt-10">
                <p className="accento text-2xl text-inchiostro/85">
                  {t('collections.plainTitle')} · {others.length}
                </p>
                <p className="prosa mt-3 max-w-xl text-sm">{t('collections.plainNote')}</p>
                <div className="mt-7 flex flex-wrap gap-2">
                  {others.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/collezioni/${c.slug}`}
                      data-testid={`collection-chip-${c.slug}`}
                      className="group border border-inchiostro/16 px-3.5 py-2 text-[14px] font-semibold transition-colors hover:border-marsala hover:bg-marsala hover:text-carta"
                    >
                      {c.name}
                      <span className="cifre ml-2 text-inchiostro/65 transition-colors group-hover:text-carta">{c.product_count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* chi cerca un oggetto, non una collezione, deve trovare la strada */}
      <section data-testid="catalog-bridge" className="wrap py-16">
        <h2 className="font-display text-2xl md:text-3xl">{t('collections.bridgeTitle')}</h2>
        <p className="prosa mt-3 max-w-xl">{t('collections.bridgeBody')}</p>
        <Link className="btn btn-solid mt-6 inline-flex" to="/catalogo">
          {t('collections.bridgeCta')}
        </Link>
      </section>
    </>
  );
}
